import { Component, computed, DestroyRef, inject, input, model, output } from '@angular/core';
import { verticalExpansion } from '../../utils/animations';
import { FormatterService } from '../../woo_services.module/FormatterService';
import { Targeting, TargetingMetaData } from '../../woo_services.module/shared-types';
import { TargetingService } from '../../woo_services.module/TargetingService';

type TargetingRecord = Record<TargetingName, string>;

@Component({
  selector: 'expandable-targeting-header',
  templateUrl: 'expandable-targeting-header.component.html',
  styleUrls: ['./expandable-targeting-header.component.scss'],
  animations: [verticalExpansion(500)],
  standalone: false,
})
export class ExpandableTargetingHeader {
  private readonly destroyRef = inject(DestroyRef);
  targeting = input<Targeting>();
  targetingMetaData = input<TargetingMetaData>();
  expanded = model<boolean>(true);
  expandedChange = output<boolean>();
  onExpandEnd = output<void>();

  private destroyed = false;

  targetingNamesData = computed(() => {
    const t = this.targeting();
    const tg = this.targetingService.getTargetGroupNames(this.targeting()).map((tg) => ({ name: tg }));
    if (!t) return {} as TargetingRecord;
    return {
      regions: t.regions.map((i) => i.name).join(', '),
      programFormats: t.program_formats.map((i) => i.name).join(', '),
      targetGroups: tg.map((i) => i.name).join(', '),
      categories: t.categories.map((i) => i.name).join(', '),
      deviceGroups: t.device_groups.map((i) => i.name).join(', '),
      behaviors: t.behaviors.map((i) => i.name).join(', '),
      dayparts: t.dayparts.map((i) => i.name).join(', '),
      advancedTargetGroups: t.advanced_target_groups.map((i) => i.name).join(', '),
    };
  });

  targetingNames = computed(() => {
    const data = this.targetingNamesData();
    return Object.keys(data ?? {}).filter((item) => !!data[item as keyof TargetingRecord]);
  });

  targetingMetaDataComp = computed(() => {
    return this.targetingMetaData() ?? this.targetingService.getMetaData(this.targeting());
  });

  targetingLabel = computed(() => {
    return this.formatterService.getTargetingLabel(this.targeting(), this.targetingMetaDataComp());
  });

  constructor(private formatterService: FormatterService, private targetingService: TargetingService) {
    this.destroyRef.onDestroy(() => (this.destroyed = true));
  }

  toggleExpanded(): void {
    this.expanded.update((e) => (e = !e));
    this.expandedChange.emit(this.expanded());
  }

  onAnimDone(): void {
    if (!this.destroyed) this.onExpandEnd.emit();
  }
}

enum TargetingName {
  regions = 'regions',
  programFormats = 'programFormats',
  advancedTargetGroups = 'advancedTargetGroups',
  targetGroups = 'targetGroups',
  categories = 'categories',
  deviceGroups = 'deviceGroups',
  behaviors = 'behaviors',
  dayparts = 'dayparts',
}
